/** @jsx jsx */
import React, { useRef, useState } from 'react';
import { css, styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { DropdownItem as MeatballsMenuItem } from '@atlaskit/dropdown-menu';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useInsightFeedbackCollector,
	OBJECT_VIEW_LOCATION,
} from '@atlassian/jira-servicedesk-insight-common-cmdb-feedback-collector/src/index.tsx';
import { QrCodeIcon } from '@atlassian/jira-servicedesk-insight-glyphs/src/index.tsx';
import { AsyncCloneObjectModal } from '@atlassian/jira-servicedesk-insight-object-clone-modal/src/async.tsx';
import { AsyncDeleteObjectModal } from '@atlassian/jira-servicedesk-insight-object-delete-modal/src/async.tsx';
import {
	useObjectStoreState,
	useObjectSchemaId,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { AsyncSingleQrCodeModal } from '@atlassian/jira-servicedesk-insight-object-qr-code-modal/src/async.tsx';
import {
	toSchemaId,
	toCmdbObjectTypeId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { BackLink } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/back-link/index.tsx';
import { MeatballsMenu } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/meatballs-menu/main.tsx';
import { createObjectSchemaUrl } from '@atlassian/jira-servicedesk-insight-urls/src/index.tsx';
import { InsightObjectViewHeaderSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/InsightObjectViewSkeleton.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { BackButtonProps } from '../../common/types.tsx';
import { Breadcrumbs } from './breadcrumbs/index.tsx';
import { IconEditDialog } from './icon-edit-dialog/index.tsx';
import messages from './messages.tsx';
import { ObjectNameInlineEdit } from './name-inline-edit/index.tsx';

export type Props = {
	isMultiColumn: boolean;
	canEdit: boolean;
	canDelete: boolean;
	backButton?: BackButtonProps;
	showFullBreadcrumbTrail?: boolean;
	redirectOnDelete: boolean;
	onClone: () => void;
	onDelete: () => void;
};

export const ObjectViewHeader = (props: Props) => {
	const {
		canEdit,
		canDelete,
		backButton,
		isMultiColumn,
		onDelete,
		onClone,
		redirectOnDelete,
		showFullBreadcrumbTrail = true,
	} = props;

	const { formatMessage } = useIntl();
	const [{ loading, objectDetails, hideEmptyAttributes }, { setHideEmptyAttributesState }] =
		useObjectStoreState();

	const [objectSchemaId] = useObjectSchemaId();

	const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isCloneModalOpen, setCloneModalOpen] = useState<boolean>(false);
	const [isQrCodeModalOpen, setQrCodeModalOpen] = useState<boolean>(false);
	const returnFocusRef = useRef<HTMLElement>(null);
	const showFullTrail = backButton === undefined ? showFullBreadcrumbTrail : !backButton;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openFeedbackDialog } = useInsightFeedbackCollector(OBJECT_VIEW_LOCATION);

	if (loading || !objectDetails || !objectSchemaId) {
		return <InsightObjectViewHeaderSkeleton />;
	}

	const renderHideEmptyAttributesMeatballs = () => {
		if (hideEmptyAttributes === true) {
			return (
				<MeatballsMenuItem
					onClick={() => {
						setHideEmptyAttributesState(false);
					}}
				>
					{formatMessage(messages.showEmptyAttributes)}
				</MeatballsMenuItem>
			);
		}
		return (
			<MeatballsMenuItem
				onClick={() => {
					setHideEmptyAttributesState(true);
				}}
			>
				{formatMessage(messages.hideEmptyAttributes)}
			</MeatballsMenuItem>
		);
	};

	const MenuContent = (
		<>
			{renderHideEmptyAttributesMeatballs()}
			<MeatballsMenuItem
				data-testId="servicedesk-insight-object-view.ui.header.meatballs-menu-clone"
				data-test-id="servicedesk-insight-object-view.ui.header.meatballs-menu-clone"
				onClick={() => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						'button clicked',
						'openInsightObjectCloneModal',
					);
					setCloneModalOpen(true);
				}}
			>
				{formatMessage(messages.clone)}
			</MeatballsMenuItem>
			{canDelete && (
				<MeatballsMenuItem
					data-testId="servicedesk-insight-object-view.ui.header.meatballs-menu-delete"
					data-test-id="servicedesk-insight-object-view.ui.header.meatballs-menu-delete"
					onClick={() => {
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'button clicked',
							'openInsightObjectDeleteModal',
						);
						setDeleteModalOpen(true);
					}}
				>
					{formatMessage(messages.delete)}
				</MeatballsMenuItem>
			)}
		</>
	);

	return (
		<>
			<div css={[preHeaderContainerStyle]}>
				{backButton && (
					<BackLink to={backButton.to} onClick={backButton.onClick}>
						{backButton.label}
					</BackLink>
				)}
				<Breadcrumbs showFullTrail={showFullTrail} />
			</div>
			<div css={[headerContainerBaseStyle, isMultiColumn && headerContainerMultiColumnStyle]}>
				<ObjectNameContainer>
					<div css={[iconContainerStyle]}>
						<IconEditDialog canEdit={canEdit} avatar={objectDetails.avatar} />
					</div>
					<div css={[inlineEditContainerStyle]}>
						<ObjectNameInlineEdit canEdit={canEdit} />
					</div>
				</ObjectNameContainer>
				<ul css={[actionContainerBaseStyle]}>
					<li>
						<Button
							appearance="subtle"
							iconBefore={
								<FeedbackIcon
									spacing="spacious"
									label={formatMessage(messages.giveFeedbackLabel)}
								/>
							}
							onClick={() => openFeedbackDialog()}
							testId="servicedesk-insight-object-view.ui.header.feedback-button"
						/>
					</li>
					<li>
						<Button
							appearance="subtle"
							iconBefore={<QrCodeIcon label={formatMessage(messages.printQrCodeLabel)} />}
							onClick={() => setQrCodeModalOpen(true)}
							testId="servicedesk-insight-object-view.ui.header.qrcode-button"
						/>
					</li>
					{canEdit ? (
						<li>
							{fg('assets-a11y-fix') ? (
								<MeatballsMenu
									moreIconLabel={formatMessage(messages.moreActions)}
									testId="servicedesk-insight-object-view.ui.header.meatballs-menu"
									ref={returnFocusRef}
								>
									{MenuContent}
								</MeatballsMenu>
							) : (
								<MeatballsMenu
									moreIconLabel={formatMessage(messages.moreActions)}
									testId="servicedesk-insight-object-view.ui.header.meatballs-menu"
								>
									{MenuContent}
								</MeatballsMenu>
							)}
						</li>
					) : null}
				</ul>
			</div>
			<AsyncDeleteObjectModal
				returnFocusRef={returnFocusRef}
				objectId={objectDetails.id}
				objectLabel={objectDetails.label}
				onSuccessRedirectUrl={
					redirectOnDelete ? createObjectSchemaUrl(toSchemaId(objectSchemaId)) : undefined
				}
				onSuccess={onDelete}
				onClose={() => setDeleteModalOpen(false)}
				isDeleteModalOpen={isDeleteModalOpen}
			/>
			<AsyncCloneObjectModal
				returnFocusRef={returnFocusRef}
				objectId={objectDetails.id}
				objectDetails={objectDetails}
				objectSchemaId={toSchemaId(objectSchemaId)}
				objectTypeId={toCmdbObjectTypeId(objectDetails.objectType.id)}
				onSuccess={onClone}
				onClose={() => setCloneModalOpen(false)}
				isCloneModalOpen={isCloneModalOpen}
			/>

			<AsyncSingleQrCodeModal
				objectId={objectDetails.id}
				objectSchemaId={toSchemaId(objectSchemaId)}
				objectTypeId={toCmdbObjectTypeId(objectDetails.objectType.id)}
				onClose={() => setQrCodeModalOpen(false)}
				isQrCodeModalOpen={isQrCodeModalOpen}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectNameContainer = styled.div({
	boxSizing: 'border-box',
	display: 'flex',
	width: '100%',
	wordBreak: 'break-all',
});

const actionContainerBaseStyle = css({
	display: 'flex',
	justifyContent: 'flex-end',
	boxSizing: 'border-box',
	gap: token('space.200'),
	flexShrink: 0,
	paddingLeft: 0,
	listStyleType: 'none',
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> li': {
		marginTop: 0,
	},
});

const headerContainerBaseStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: `${token('space.200')}`,
	marginTop: token('space.200'),
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: 0,
	paddingTop: `${token('space.100')}`,
	paddingLeft: `${token('space.100')}`,
	flexWrap: 'wrap',
});

const headerContainerMultiColumnStyle = css({
	flexWrap: 'nowrap',
	paddingTop: 0,
	gap: `${token('space.500')}`,
});

const inlineEditContainerStyle = css({
	flex: 1,
	/* override AK default margin applied to inline edit */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'form > div': {
		marginTop: 0,
	},
});

const iconContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
	marginRight: token('space.100'),
});

const preHeaderContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
	paddingLeft: token('space.100'),
	/* Prevents layout shifts when switching between Breadcrumsb/BackButton view states */

	minHeight: token('space.400'),
});
